/**
 * Advanced Lazy Loading - Autumn Field Nursery
 * Intersection Observer-based lazy loading with:
 * - CSS background-image lazy loading
 * - Blur-up / fade-in transition for images
 * - Low-priority prefetching
 */
class LazyLoader {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: load all immediately
      this.loadAllFallback();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            
            // Handle CSS background images
            if (target.dataset.bg) {
              this.loadBackground(target);
            }
            
            // Handle blur-up images
            if (target.dataset.src) {
              this.loadBlurUp(target);
            }
            
            // Handle video posters
            if (target.dataset.poster) {
              this.loadVideoPoster(target);
            }
            
            this.observer.unobserve(target);
          }
        });
      },
      {
        rootMargin: '200px 0px', // Start loading 200px before visible
        threshold: 0.01,
      }
    );

    this.observeElements();
  }

  observeElements() {
    // Observe CSS background elements
    document.querySelectorAll('[data-bg]').forEach((el) => {
      this.observer.observe(el);
    });

    // Observe blur-up images
    document.querySelectorAll('img[data-src]').forEach((img) => {
      this.observer.observe(img);
    });

    // Observe video posters
    document.querySelectorAll('[data-poster]').forEach((el) => {
      this.observer.observe(el);
    });
  }

  loadBackground(el) {
    const bgUrl = el.dataset.bg;
    const bgSize = el.dataset.bgSize || 'cover';
    const bgPosition = el.dataset.bgPosition || 'center';
    
    // Create a temp image to preload
    const img = new Image();
    img.onload = () => {
      el.style.backgroundImage = `url(${bgUrl})`;
      el.style.backgroundSize = bgSize;
      el.style.backgroundPosition = bgPosition;
      el.style.backgroundRepeat = 'no-repeat';
      el.classList.add('bg-loaded');
      delete el.dataset.bg;
    };
    img.onerror = () => {
      // If fails, try to set anyway
      el.style.backgroundImage = `url(${bgUrl})`;
      el.classList.add('bg-loaded');
      delete el.dataset.bg;
    };
    img.src = bgUrl;
  }

  loadBlurUp(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    const sizes = img.dataset.sizes;
    
    // Create temp image to preload
    const tempImg = new Image();
    
    tempImg.onload = () => {
      img.src = src;
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      img.classList.add('loaded');
      
      delete img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.sizes;
    };
    
    tempImg.onerror = () => {
      // On error, still try to set source
      img.src = src;
      img.classList.add('loaded');
    };
    
    tempImg.src = src;
  }

  loadVideoPoster(el) {
    const poster = el.dataset.poster;
    const tempImg = new Image();
    tempImg.onload = () => {
      el.poster = poster;
      delete el.dataset.poster;
    };
    tempImg.src = poster;
  }

  loadAllFallback() {
    document.querySelectorAll('[data-bg]').forEach((el) => {
      el.style.backgroundImage = `url(${el.dataset.bg})`;
      delete el.dataset.bg;
    });
    document.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      delete img.dataset.src;
      delete img.dataset.srcset;
    });
  }

  // Public method to re-scan after dynamic content changes
  refresh() {
    if (this.observer) {
      this.observeElements();
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.lazyLoader = new LazyLoader();
});

// Expose for Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.lazyLoader) {
      window.lazyLoader.refresh();
    }
  });
}
